import { Check } from 'lucide-react';

export type StepIndicatorItem = {
  id: string;
  label: string;
};

type StepIndicatorProps = {
  steps: StepIndicatorItem[];
  currentStepId: string;
  testId: string;
  ariaLabel?: string;
};

function circleClasses(isComplete: boolean, isCurrent: boolean): string {
  if (isComplete) {
    return 'bg-prim-600 text-neut-0 border-prim-600';
  }
  if (isCurrent) {
    return 'bg-neut-0 text-prim-700 border-prim-600 ring-4 ring-prim-100';
  }
  return 'bg-neut-0 text-neut-400 border-neut-300';
}

export function StepIndicator({
  steps,
  currentStepId,
  testId,
  ariaLabel,
}: Readonly<StepIndicatorProps>) {
  const currentIndex = steps.findIndex((step) => step.id === currentStepId);

  return (
    <ol
      data-testid={testId}
      aria-label={ariaLabel}
      className="flex items-center gap-3 sm:gap-4"
    >
      {steps.map((step, index) => {
        const isComplete = index < currentIndex;
        const isCurrent = index === currentIndex;

        return (
          <li
            key={step.id}
            aria-current={isCurrent ? 'step' : undefined}
            className="flex items-center gap-3 sm:gap-4 last:flex-none flex-1"
          >
            <span className="flex items-center gap-2.5 whitespace-nowrap">
              <span
                aria-hidden="true"
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors ${circleClasses(
                  isComplete,
                  isCurrent
                )}`}
              >
                {isComplete ? <Check size={16} strokeWidth={3} /> : index + 1}
              </span>
              <span
                className={`text-sm transition-colors ${
                  isCurrent
                    ? 'font-semibold text-neut-prim'
                    : 'font-medium text-neut-sec'
                }`}
              >
                {step.label}
              </span>
            </span>
            {index < steps.length - 1 && (
              <span
                aria-hidden="true"
                className={`h-px flex-1 rounded-full transition-colors ${
                  isComplete ? 'bg-prim-500' : 'bg-neut-200'
                }`}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
